import React, { useState, useEffect, useCallback } from 'react';
import ApiClient, { AISettings, AIModel, EmailSettings, SESRegion, RateLimitSettings, MaxMindConfig } from '../api/client';
import { usePermissions } from '../context/PermissionContext';

interface SettingsProps {
  apiClient: ApiClient;
}

type AIProvider = 'anthropic' | 'openai';
type SettingsTab = 'integrations' | 'protection' | 'maintenance';

const Settings: React.FC<SettingsProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('settings.manage');

  // AI Settings - provider-first approach
  const [aiSettings, setAISettings] = useState<AISettings | null>(null);
  const [aiModels, setAIModels] = useState<AIModel[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>('anthropic');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [aiSaving, setAISaving] = useState(false);
  const [aiError, setAIError] = useState<string | null>(null);
  const [aiSuccess, setAISuccess] = useState<string | null>(null);

  // Email Settings
  const [emailSettings, setEmailSettings] = useState<EmailSettings | null>(null);
  const [emailRegions, setEmailRegions] = useState<SESRegion[]>([]);
  const [emailAccessKey, setEmailAccessKey] = useState('');
  const [emailSecretKey, setEmailSecretKey] = useState('');
  const [emailRegion, setEmailRegion] = useState('us-east-1');
  const [emailFromEmail, setEmailFromEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Rate Limiting Settings
  const [rateLimitSettings, setRateLimitSettings] = useState<RateLimitSettings | null>(null);
  const [perIpPerMinute, setPerIpPerMinute] = useState(10);
  const [perFormPerMinute, setPerFormPerMinute] = useState(100);
  const [blacklistThreshold, setBlacklistThreshold] = useState(50);
  const [rateLimitSaving, setRateLimitSaving] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const [rateLimitSuccess, setRateLimitSuccess] = useState<string | null>(null);

  // MaxMind Settings
  const [maxMindConfig, setMaxMindConfig] = useState<MaxMindConfig | null>(null);
  const [maxMindAccountId, setMaxMindAccountId] = useState('');
  const [maxMindLicenseKey, setMaxMindLicenseKey] = useState('');
  const [maxMindThreshold, setMaxMindThreshold] = useState(5);
  const [maxMindSaving, setMaxMindSaving] = useState(false);
  const [maxMindUpdating, setMaxMindUpdating] = useState(false);
  const [maxMindError, setMaxMindError] = useState<string | null>(null);
  const [maxMindSuccess, setMaxMindSuccess] = useState<string | null>(null);

  // Data Maintenance
  const [dataMaintenance, setDataMaintenance] = useState<{
    running: string | null;
    error: string | null;
    success: string | null;
  }>({ running: null, error: null, success: null });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<SettingsTab>('integrations');

  // Fallback models if API doesn't support the models endpoint yet
  const FALLBACK_MODELS: AIModel[] = [
    { code: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5 (recommended)', provider: 'anthropic' },
    { code: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5 (faster)', provider: 'anthropic' },
    { code: 'claude-opus-4-5-20251101', name: 'Claude Opus 4.5 (most capable)', provider: 'anthropic' },
  ];

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      // Load models separately with fallback
      let models: AIModel[] = FALLBACK_MODELS;
      try {
        models = await apiClient.getAIModels();
      } catch (e) {
        console.log('Models endpoint not available, using fallback');
      }

      const [ai, email, regions, rateLimit, maxMind] = await Promise.all([
        apiClient.getAISettings(),
        apiClient.getEmailSettings(),
        apiClient.getEmailRegions(),
        apiClient.getRateLimitSettings(),
        apiClient.getMaxMindConfig().catch(() => null),
      ]);
      setAISettings(ai);
      setAIModels(models);
      setEmailSettings(email);
      setEmailRegions(regions);
      setRateLimitSettings(rateLimit);
      if (maxMind) {
        setMaxMindConfig(maxMind);
        setMaxMindThreshold(maxMind.threshold);
      }

      // Set initial provider and model from current settings
      if (ai.active_provider) {
        setSelectedProvider(ai.active_provider as AIProvider);
      }
      if (ai.active_model) {
        setSelectedModel(ai.active_model);
      } else {
        // Default to first model of selected provider
        const providerModels = models.filter(m => m.provider === (ai.active_provider || 'anthropic'));
        if (providerModels.length > 0) {
          setSelectedModel(providerModels[0].code);
        }
      }

      if (email.region) setEmailRegion(email.region);
      if (email.from_email) setEmailFromEmail(email.from_email);
      if (rateLimit) {
        setPerIpPerMinute(rateLimit.per_ip_per_minute);
        setPerFormPerMinute(rateLimit.per_form_per_minute);
        setBlacklistThreshold(rateLimit.blacklist_threshold);
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Helper: check if a specific provider is configured
  const isProviderConfigured = (provider: AIProvider): boolean => {
    if (!aiSettings?.providers) return false;
    return aiSettings.providers[provider]?.configured ?? false;
  };

  // Helper: check if any AI is configured
  const isAIConfigured = (): boolean => {
    return aiSettings?.active_provider !== null && aiSettings?.active_model !== null;
  };

  // Get models for the selected provider
  const getProviderModels = (provider: AIProvider): AIModel[] => {
    return aiModels.filter(m => m.provider === provider);
  };

  // Handle provider change
  const handleProviderChange = (provider: AIProvider) => {
    setSelectedProvider(provider);
    setApiKeyInput('');
    setAIError(null);
    setAISuccess(null);
    // Set default model for the new provider
    const providerModels = getProviderModels(provider);
    if (providerModels.length > 0) {
      setSelectedModel(providerModels[0].code);
    }
  };

  // Configure a provider's API key
  const handleConfigureProvider = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKeyInput.trim()) {
      setAIError('API key is required');
      return;
    }

    setAISaving(true);
    setAIError(null);
    setAISuccess(null);

    try {
      await apiClient.configureAIProvider(selectedProvider, apiKeyInput);
      const updatedSettings = await apiClient.getAISettings();
      setAISettings(updatedSettings);
      setApiKeyInput('');
      setAISuccess(`${selectedProvider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API key configured successfully!`);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to configure provider';
      setAIError(message);
    } finally {
      setAISaving(false);
    }
  };

  // Remove a provider's API key
  const handleRemoveProvider = async (provider: AIProvider) => {
    if (!window.confirm(`Remove ${provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API key?`)) {
      return;
    }

    setAISaving(true);
    setAIError(null);
    setAISuccess(null);

    try {
      await apiClient.removeAIProvider(provider);
      const updatedSettings = await apiClient.getAISettings();
      setAISettings(updatedSettings);
      setAISuccess(`${provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API key removed.`);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to remove provider';
      setAIError(message);
    } finally {
      setAISaving(false);
    }
  };

  // Set active model (and implicitly the provider)
  const handleSaveActiveModel = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isProviderConfigured(selectedProvider)) {
      setAIError('Please configure the API key first');
      return;
    }

    setAISaving(true);
    setAIError(null);
    setAISuccess(null);

    try {
      const result = await apiClient.updateAISettings({ active_model: selectedModel });
      setAISettings(result);
      setAISuccess('AI model updated successfully!');
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to update AI settings';
      setAIError(message);
    } finally {
      setAISaving(false);
    }
  };

  const handleSaveEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailAccessKey.trim() || !emailSecretKey.trim() || !emailFromEmail.trim()) {
      setEmailError('All fields are required');
      return;
    }

    setEmailSaving(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const result = await apiClient.updateEmailSettings({
        access_key: emailAccessKey,
        secret_key: emailSecretKey,
        region: emailRegion,
        from_email: emailFromEmail,
      });
      setEmailSettings({
        provider: result.provider,
        region: result.region,
        from_email: result.from_email,
        configured: result.configured,
      });
      setEmailAccessKey('');
      setEmailSecretKey('');
      setEmailSuccess('Email provider configured successfully!');
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to save email settings';
      setEmailError(message);
    } finally {
      setEmailSaving(false);
    }
  };

  const handleSaveRateLimit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRateLimitSaving(true);
    setRateLimitError(null);
    setRateLimitSuccess(null);

    try {
      const result = await apiClient.updateRateLimitSettings({
        per_ip_per_minute: perIpPerMinute,
        per_form_per_minute: perFormPerMinute,
        blacklist_threshold: blacklistThreshold,
      });
      setRateLimitSettings(result);
      setRateLimitSuccess('Rate limiting settings saved!');
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to save rate limit settings';
      setRateLimitError(message);
    } finally {
      setRateLimitSaving(false);
    }
  };

  const handleSaveMaxMind = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!maxMindAccountId.trim() || !maxMindLicenseKey.trim()) {
      setMaxMindError('Account ID and License Key are required');
      return;
    }

    setMaxMindSaving(true);
    setMaxMindError(null);
    setMaxMindSuccess(null);

    try {
      await apiClient.updateMaxMindConfig(
        maxMindAccountId.trim(),
        maxMindLicenseKey.trim(),
        maxMindThreshold
      );
      // Reload full config to get databaseExists status
      const config = await apiClient.getMaxMindConfig();
      setMaxMindConfig(config);
      setMaxMindAccountId('');
      setMaxMindLicenseKey('');
      setMaxMindSuccess('MaxMind configuration saved!');
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to save MaxMind settings';
      setMaxMindError(message);
    } finally {
      setMaxMindSaving(false);
    }
  };

  const handleUpdateMaxMindDatabase = async () => {
    setMaxMindUpdating(true);
    setMaxMindError(null);
    setMaxMindSuccess(null);

    try {
      const result = await apiClient.updateMaxMindDatabase();
      setMaxMindSuccess(result.message || 'Database updated successfully!');
      // Reload config to get updated state
      const config = await apiClient.getMaxMindConfig();
      setMaxMindConfig(config);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to update database';
      setMaxMindError(message);
    } finally {
      setMaxMindUpdating(false);
    }
  };

  const handleRemoveMaxMind = async () => {
    if (!window.confirm('Remove MaxMind configuration? ASN data will no longer be available.')) {
      return;
    }

    setMaxMindSaving(true);
    setMaxMindError(null);
    setMaxMindSuccess(null);

    try {
      await apiClient.deleteMaxMindConfig();
      setMaxMindConfig({ configured: false, databaseExists: false, threshold: 5 });
      setMaxMindSuccess('MaxMind configuration removed.');
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to remove configuration';
      setMaxMindError(message);
    } finally {
      setMaxMindSaving(false);
    }
  };

  // Data Maintenance handlers
  const handleBackfillIps = async () => {
    setDataMaintenance({ running: 'backfillIps', error: null, success: null });
    try {
      const result = await apiClient.backfillIps();
      setDataMaintenance({
        running: null,
        error: null,
        success: `Backfill complete: ${result.ipsCreated} IPs created, ${result.asnsCreated} ASNs created`,
      });
    } catch (err: any) {
      setDataMaintenance({
        running: null,
        error: err.message || 'Failed to backfill IPs',
        success: null,
      });
    }
  };

  const handleRefreshStats = async () => {
    setDataMaintenance({ running: 'refreshStats', error: null, success: null });
    try {
      const result = await apiClient.refreshStats();
      setDataMaintenance({
        running: null,
        error: null,
        success: `Stats refreshed: ${result.ipsUpdated} IPs, ${result.asnsCreated + result.asnsUpdated} ASNs updated`,
      });
    } catch (err: any) {
      setDataMaintenance({
        running: null,
        error: err.message || 'Failed to refresh stats',
        success: null,
      });
    }
  };

  const handleBackfillASN = async () => {
    setDataMaintenance({ running: 'backfillASN', error: null, success: null });
    try {
      const result = await apiClient.backfillASN();
      setDataMaintenance({
        running: null,
        error: null,
        success: `ASN data updated: ${result.updated} of ${result.processed} IPs`,
      });
    } catch (err: any) {
      setDataMaintenance({
        running: null,
        error: err.message || 'Failed to backfill ASN data',
        success: null,
      });
    }
  };

  const handleBackfillCountry = async () => {
    setDataMaintenance({ running: 'backfillCountry', error: null, success: null });
    try {
      const result = await apiClient.backfillCountry();
      setDataMaintenance({
        running: null,
        error: null,
        success: `Country data updated: ${result.updated} of ${result.processed} submissions`,
      });
    } catch (err: any) {
      setDataMaintenance({
        running: null,
        error: err.message || 'Failed to backfill country data',
        success: null,
      });
    }
  };

  const handleSyncData = async () => {
    setDataMaintenance({ running: 'syncData', error: null, success: null });
    try {
      const backfillResult = await apiClient.backfillIps();
      const refreshResult = await apiClient.refreshStats();
      setDataMaintenance({
        running: null,
        error: null,
        success: `Sync complete: ${backfillResult.ipsCreated} IPs created, ${refreshResult.ipsUpdated} IPs updated, ${refreshResult.asnsCreated + refreshResult.asnsUpdated} ASNs processed`,
      });
    } catch (err: any) {
      setDataMaintenance({
        running: null,
        error: err.message || 'Failed to sync data',
        success: null,
      });
    }
  };

  if (loading) {
    return (
      <div className="manager-page settings-page">
        <div className="empty-state">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-page settings-page">
      <div className="list-page-header">
        <div className="list-page-title">
          <h2>Settings</h2>
        </div>
        <div className="list-page-actions">
          <button className="btn btn-secondary" onClick={loadSettings}>
            Refresh
          </button>
        </div>
      </div>

      <div className="settings-shell">
        <div className="local-tabs">
          <div className="local-tabs-primary">
            <button className={activeTab === 'integrations' ? 'active' : ''} onClick={() => setActiveTab('integrations')}>
              Integrations
            </button>
            <button className={activeTab === 'protection' ? 'active' : ''} onClick={() => setActiveTab('protection')}>
              Protection
            </button>
            <button className={activeTab === 'maintenance' ? 'active' : ''} onClick={() => setActiveTab('maintenance')}>
              Maintenance
            </button>
          </div>
        </div>

        <div className="settings-tab-panel">
          {activeTab === 'integrations' && (
            <div className="settings-grid">
              <section className="settings-card">
                <div className="settings-card-header">
                  <h3>AI Provider</h3>
                  <span className={`settings-status ${isAIConfigured() ? 'success' : 'warning'}`}>
                    {isAIConfigured() ? 'Active' : 'Not configured'}
                  </span>
                </div>
                <div className="settings-summary">
                  {isAIConfigured() ? `Using ${aiSettings?.active_model || 'model not set'}` : 'Spam detection is disabled.'}
                </div>
                {aiError && <div className="alert alert-error">{aiError}</div>}
                {aiSuccess && <div className="alert alert-success">{aiSuccess}</div>}
                {canManage && (
                  <>
                    <div className="form-group">
                      <label>Provider</label>
                      <div className="settings-segmented">
                        <button type="button" className={selectedProvider === 'anthropic' ? 'active' : ''} onClick={() => handleProviderChange('anthropic')}>
                          Anthropic {isProviderConfigured('anthropic') && <span>configured</span>}
                        </button>
                        <button type="button" className={selectedProvider === 'openai' ? 'active' : ''} onClick={() => handleProviderChange('openai')}>
                          OpenAI {isProviderConfigured('openai') && <span>configured</span>}
                        </button>
                      </div>
                    </div>
                    {!isProviderConfigured(selectedProvider) ? (
                      <form onSubmit={handleConfigureProvider}>
                        <div className="form-group">
                          <label>{selectedProvider === 'anthropic' ? 'Anthropic' : 'OpenAI'} API Key</label>
                          <input
                            type="password"
                            value={apiKeyInput}
                            onChange={(e) => setApiKeyInput(e.target.value)}
                            placeholder={selectedProvider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
                          />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={aiSaving || !apiKeyInput.trim()}>
                          {aiSaving ? 'Validating...' : 'Configure API Key'}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleSaveActiveModel}>
                        <div className="form-group">
                          <label>Model</label>
                          <select value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                            {getProviderModels(selectedProvider).map((model) => (
                              <option key={model.code} value={model.code}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="settings-actions-row">
                          <button type="submit" className="btn btn-primary" disabled={aiSaving}>
                            {aiSaving ? 'Saving...' : 'Use This Model'}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => handleRemoveProvider(selectedProvider)} disabled={aiSaving}>
                            Remove Key
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}
              </section>

              <section className="settings-card">
                <div className="settings-card-header">
                  <h3>Email Provider</h3>
                  <span className={`settings-status ${emailSettings?.configured ? 'success' : 'warning'}`}>
                    {emailSettings?.configured ? 'Configured' : 'Not configured'}
                  </span>
                </div>
                <div className="settings-summary">
                  {emailSettings?.configured ? `${emailSettings.from_email} (${emailSettings.region})` : 'Email notifications are disabled.'}
                </div>
                {emailError && <div className="alert alert-error">{emailError}</div>}
                {emailSuccess && <div className="alert alert-success">{emailSuccess}</div>}
                {canManage && (
                  <form onSubmit={handleSaveEmail}>
                    <div className="settings-form-grid two">
                      <div className="form-group">
                        <label>AWS Access Key ID</label>
                        <input type="password" value={emailAccessKey} onChange={(e) => setEmailAccessKey(e.target.value)} placeholder={emailSettings?.configured ? '••••••••••••••••' : 'AKIA...'} />
                      </div>
                      <div className="form-group">
                        <label>AWS Secret Access Key</label>
                        <input type="password" value={emailSecretKey} onChange={(e) => setEmailSecretKey(e.target.value)} placeholder="Secret key" />
                      </div>
                      <div className="form-group">
                        <label>AWS Region</label>
                        <select value={emailRegion} onChange={(e) => setEmailRegion(e.target.value)}>
                          {emailRegions.map((region) => (
                            <option key={region.code} value={region.code}>{region.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>From Email Address</label>
                        <input type="email" value={emailFromEmail} onChange={(e) => setEmailFromEmail(e.target.value)} placeholder="noreply@example.com" />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={emailSaving || !emailAccessKey.trim() || !emailSecretKey.trim() || !emailFromEmail.trim()}>
                      {emailSaving ? 'Validating...' : 'Save Email Settings'}
                    </button>
                  </form>
                )}
              </section>
            </div>
          )}

          {activeTab === 'protection' && (
            <div className="settings-stack">
              <section className="settings-card">
                <div className="settings-card-header">
                  <h3>Rate Limiting</h3>
                  {rateLimitSettings && <span className="settings-status success">Configured</span>}
                </div>
                {rateLimitError && <div className="alert alert-error">{rateLimitError}</div>}
                {rateLimitSuccess && <div className="alert alert-success">{rateLimitSuccess}</div>}
                {canManage && (
                  <form onSubmit={handleSaveRateLimit}>
                    <div className="settings-form-grid three">
                      <div className="form-group">
                        <label>Requests per IP/min</label>
                        <input type="number" min="1" value={perIpPerMinute} onChange={(e) => setPerIpPerMinute(parseInt(e.target.value) || 1)} />
                      </div>
                      <div className="form-group">
                        <label>Requests per form/min</label>
                        <input type="number" min="1" value={perFormPerMinute} onChange={(e) => setPerFormPerMinute(parseInt(e.target.value) || 1)} />
                      </div>
                      <div className="form-group">
                        <label>Auto-blacklist threshold</label>
                        <input type="number" min="1" value={blacklistThreshold} onChange={(e) => setBlacklistThreshold(parseInt(e.target.value) || 1)} />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={rateLimitSaving}>
                      {rateLimitSaving ? 'Saving...' : 'Save Rate Limits'}
                    </button>
                  </form>
                )}
              </section>

              <section className="settings-card">
                <div className="settings-card-header">
                  <h3>MaxMind ASN</h3>
                  <span className={`settings-status ${maxMindConfig?.configured ? 'success' : 'warning'}`}>
                    {maxMindConfig?.configured ? (maxMindConfig.databaseExists ? 'Ready' : 'Needs database') : 'Not configured'}
                  </span>
                </div>
                <div className="settings-summary">
                  Bad reputation threshold: {maxMindConfig?.threshold ?? maxMindThreshold} blacklisted IPs.
                </div>
                {maxMindError && <div className="alert alert-error">{maxMindError}</div>}
                {maxMindSuccess && <div className="alert alert-success">{maxMindSuccess}</div>}
                {canManage && (
                  <>
                    {maxMindConfig?.configured && (
                      <div className="settings-actions-row">
                        <button className="btn btn-primary" onClick={handleUpdateMaxMindDatabase} disabled={maxMindUpdating}>
                          {maxMindUpdating ? 'Updating...' : 'Update Database'}
                        </button>
                        <button className="btn btn-secondary" onClick={handleRemoveMaxMind} disabled={maxMindSaving}>
                          Remove Configuration
                        </button>
                      </div>
                    )}
                    <form onSubmit={handleSaveMaxMind}>
                      <div className="settings-form-grid three">
                        <div className="form-group">
                          <label>{maxMindConfig?.configured ? 'Update Account ID' : 'Account ID'}</label>
                          <input type="text" value={maxMindAccountId} onChange={(e) => setMaxMindAccountId(e.target.value)} placeholder={maxMindConfig?.configured ? 'Leave empty to keep current' : '123456'} />
                        </div>
                        <div className="form-group">
                          <label>{maxMindConfig?.configured ? 'Update License Key' : 'License Key'}</label>
                          <input type="password" value={maxMindLicenseKey} onChange={(e) => setMaxMindLicenseKey(e.target.value)} placeholder={maxMindConfig?.configured ? 'Leave empty to keep current' : 'Your license key'} />
                        </div>
                        <div className="form-group">
                          <label>Bad Reputation Threshold</label>
                          <input type="number" min="1" value={maxMindThreshold} onChange={(e) => setMaxMindThreshold(parseInt(e.target.value) || 5)} />
                        </div>
                      </div>
                      <button
                        type="submit"
                        className={maxMindConfig?.configured ? 'btn btn-secondary' : 'btn btn-primary'}
                        disabled={
                          maxMindSaving ||
                          (maxMindConfig?.configured
                            ? !maxMindAccountId.trim() && !maxMindLicenseKey.trim()
                            : !maxMindAccountId.trim() || !maxMindLicenseKey.trim())
                        }
                      >
                        {maxMindSaving ? 'Saving...' : maxMindConfig?.configured ? 'Update Credentials' : 'Configure MaxMind'}
                      </button>
                    </form>
                  </>
                )}
              </section>
            </div>
          )}

          {activeTab === 'maintenance' && (
            <section className="settings-card">
              <div className="settings-card-header">
                <h3>Data Maintenance</h3>
                {dataMaintenance.running && <span className="settings-status warning">Running</span>}
              </div>
              {dataMaintenance.error && <div className="alert alert-error">{dataMaintenance.error}</div>}
              {dataMaintenance.success && <div className="alert alert-success">{dataMaintenance.success}</div>}
              {canManage ? (
                <div className="maintenance-grid">
                  <div className="maintenance-primary">
                    <div>
                      <strong>Sync All Data</strong>
                      <span>Create missing IP/ASN records and recalculate stats from submissions.</span>
                    </div>
                    <button className="btn btn-primary" onClick={handleSyncData} disabled={dataMaintenance.running !== null}>
                      {dataMaintenance.running === 'syncData' ? 'Syncing...' : 'Sync Data'}
                    </button>
                  </div>
                  {[
                    ['Backfill IPs', 'Create IP/ASN records for submissions missing them.', handleBackfillIps, 'backfillIps', false],
                    ['Refresh Stats', 'Recalculate submission counts and ASN statistics.', handleRefreshStats, 'refreshStats', false],
                    ['Backfill ASN', 'Look up ASN data from MaxMind for IPs missing it.', handleBackfillASN, 'backfillASN', !maxMindConfig?.databaseExists],
                    ['Backfill Country', 'Look up country data for submissions missing it.', handleBackfillCountry, 'backfillCountry', !maxMindConfig?.databaseExists],
                  ].map(([title, body, action, key, blocked]) => (
                    <div className="maintenance-action" key={key as string}>
                      <strong>{title as string}</strong>
                      <span>{body as string}</span>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={action as () => void}
                        disabled={dataMaintenance.running !== null || Boolean(blocked)}
                      >
                        {dataMaintenance.running === key ? 'Running...' : 'Run'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="settings-summary warning">You do not have permission to run maintenance tasks.</div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
